import {
  ConvertKeysToSnakeCase,
  DottedJoin,
  ExperienceDto,
  ExperienceTestType,
  PluginConfigVersion,
} from "@intelligems/ig-types";

export * from "./builder-replacements-menu";
export * from "./date";
export * from "./exclusion-groups";
export * from "./experience-status";
export * from "./get-preview-path";
export * from "./google";
export * from "./groupRounder";
export * from "./shopify-id";
export * from "./useInterval";

export const fromProcess = <R = string, D = undefined>(
  key: string,
  defaultValue?: string | null | undefined
) => {
  if (typeof process !== "undefined") {
    // eslint-disable-next-line n/no-process-env
    return (process.env[key] as R) ?? (defaultValue as D);
  }
  return defaultValue as D;
};

export const shortId = (value: string): string => {
  return value.split("-").pop() ?? "";
};
export const vanillaChunk = <T>(array: T[], size: number): T[][] => {
  const chunked_arr: T[][] = [];
  let index = 0;
  while (index < array.length) {
    chunked_arr.push(array.slice(index, size + index));
    index += size;
  }
  return chunked_arr;
};

export const sleep = async (time: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, time);
  });
};

export const overwriteMerge = (
  destinationArray: any,
  options: any,
  sourceArray: any
) => sourceArray;

export const hexToRgbA = (hexCode: string, opacity = 1) => {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  /* Backward compatibility for whole number based opacity values. */
  if (opacity > 1 && opacity <= 100) {
    opacity = opacity / 100;
  }

  return `rgba(${r},${g},${b},${opacity})`;
};

/**
 * Sorts in ascending order
 */
export function sortBy<T extends { [Key in K]: number }, K extends keyof T>(
  items: T[],
  key: K
) {
  [...items].sort((a, b) => a[key] - b[key]);
  return items;
}

export const ensureNumber = (
  input: string | number | null | undefined
): number => {
  if (typeof input === "string") {
    return parseFloat(input);
  }
  return <number>input;
};

export const ensurePercent = (input: number): number => {
  if (input > 1) {
    return input / 100;
  }
  return input;
};

export const ensureString = (
  input: string | number | null | undefined
): string => {
  if (typeof input === "number") {
    return input.toString();
  }
  return <string>input;
};

export const getExperimentType = (
  experience: ExperienceDto
): ExperienceTestType => {
  if (
    !(experience.type === "shipping") &&
    !(experience.type === "pricing") &&
    !(experience.type === "personalization/offer")
  ) {
    return "content";
  } else if (experience.type === "shipping") {
    return "shipping";
  } else if (experience.type === "pricing") {
    return "pricing";
  } else if (experience.type === "personalization/offer") {
    return "campaign";
  } else {
    return "content";
  }
};

export const ensurePriceNumber = (
  input: string | number,
  priceRegex: RegExp
): number => {
  if (typeof input === "string") {
    if (priceRegex.test(input)) {
      return ensureNumber(input.match(priceRegex)![0]);
    }
    return ensureNumber(input);
  } else {
    return input;
  }
};

export const getTextColor = (hexOrRgb: string) => {
  let color;
  if (hexOrRgb.includes("#")) {
    color = hexToRgbA(hexOrRgb);
  } else {
    color = hexOrRgb;
  }
  const rgba: any = color.match(/\d+/g);
  if (rgba) {
    if (rgba[0] * 0.299 + rgba[1] * 0.587 + rgba[2] * 0.114 > 186) {
      return "black";
    } else {
      return "white";
    }
  }
  return "black";
};

export const snakeToPascalCase = (str: string) => {
  return str
    .split("/")
    .map((snake) =>
      snake
        .split("_")
        .map((substr) => substr.charAt(0).toUpperCase() + substr.slice(1))
        .join(" ")
    )
    .join("/");
};

export const camelToSnakeCase = (str: string) =>
  str
    .replace(/([a-z])([A-Z])/g, (letter, first, second) => {
      return `${first}_${second.toLowerCase()}`;
    })
    // 2nd replace is to handle inconsistent cases like updateVariantsInATCForm & interceptAtcXhr
    .replace(/([A-Z])([a-z]?)/g, (letter, first, second) => {
      if (first && !second) {
        return first.toLowerCase();
      }
      return `_${first.toLowerCase()}${second}`;
    });

export function convertKeysToSnakeCase<T extends { [key: string]: any }>(
  obj: T
): ConvertKeysToSnakeCase<T> {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnakeCase) as ConvertKeysToSnakeCase<T>;
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = camelToSnakeCase(key);
      // @ts-ignore
      acc[snakeKey] = convertKeysToSnakeCase(
        obj[key]
      ) as ConvertKeysToSnakeCase<T>;
      return acc;
    }, {} as ConvertKeysToSnakeCase<T>);
  }
  return obj;
}

export function flattenObjectRemoveParents(obj: { [key: string]: any }): {
  [key: string]: any;
} {
  const result: { [key: string]: any } = {};

  function recurse(currObj: { [key: string]: any }, parentKey: string = "") {
    for (const key in currObj) {
      // eslint-disable-next-line no-prototype-builtins
      if (currObj.hasOwnProperty(key)) {
        const newKey = parentKey ? `${parentKey}_${key}` : key;
        if (
          typeof currObj[key] === "object" &&
          currObj[key] !== null &&
          !Array.isArray(currObj[key])
        ) {
          recurse(currObj[key], "");
        } else {
          result[newKey] = currObj[key];
        }
      }
    }
  }

  recurse(obj);
  return result;
}

export const camelToPascalCase = (str: string) => {
  const spaced = str
    .replace(
      /([^A-Z])([A-Z])/g,
      (letter, nonUppercase, uppercase) =>
        `${nonUppercase} ${uppercase.toUpperCase()}`
    )
    // 2nd replace is to handle inconsistent cases like updateVariantsInATCForm & interceptAtcXhr
    .replace(/([A-Z])([a-z]?)/g, (letter, first, second) => {
      if (first && !second) {
        return first;
      }
      return ` ${first}${second}`;
    });
  return capitalizeFirstLetter(spaced);
};

export function capitalizeFirstLetter(str?: string) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export function uppercaseSnakeToNormal(str: string) {
  // Split the string by underscores
  const words = str.split("_");

  // Capitalize the first letter of each word and join them with a space
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function hasStringDuplicates(array: string[]): boolean {
  return new Set(array.map((arr) => arr.toLowerCase())).size !== array.length;
}

export class Css {
  public static setCssVariable(key: string, value: string) {
    const root = document.querySelector(":root");
    if (root) {
      if (key.startsWith("--")) {
        // @ts-ignore
        root.style.setProperty(key, value);
      } else {
        // @ts-ignore
        root.style.setProperty(`--${key}`, value);
      }
    }
  }

  public static getWidgetCssVariableName<
    T extends {
      desktop: object;
    },
  >(prefix: string, style: DottedJoin<T["desktop"]>, mobile = false) {
    // @ts-ignore
    return `--${prefix}-${style.replaceAll(".", "-")}-${mobile ? "m" : "d"}`;
  }
}

export const invertObject = <
  A extends string | number | symbol,
  B extends string | number | symbol,
>(
  obj: Record<A, B>
): Record<B, A> => {
  // @ts-ignore
  const invertedObject: Record<B, A> = {};
  for (const key in obj) {
    invertedObject[obj[key]] = key;
  }
  return invertedObject;
};

export const dotToDash = (str: string) => str.replace(/\./g, "-");
export const dotToUnderscore = (str: string) => str.replace(/\./g, "_");
export const dashToDot = (str: string) => str.replace(/-/g, ".");

export const safeJSONParse = (input: unknown) => {
  let parsedInput = input;
  if (typeof input !== "string") {
    return parsedInput;
  }
  try {
    parsedInput = JSON.parse(input);
  } catch (e) {
    // do nothing
  }
  return parsedInput;
};

export const truncateString = (str: string, limit: number): string => {
  if (str.length <= limit) {
    return str;
  }

  return str.substring(0, limit - 3) + "...";
};

export const stringToBoolean = (value: string): boolean => {
  value = value.toLowerCase();
  if (["y", "yes", "t", "true", "on", "1"].includes(value)) return true;
  if (["n", "no", "f", "false", "off", "0"].includes(value)) return false;
  throw Error(`Invalid truth value ${value}`);
};

export const HeadlessToPluginConfigMap: Record<string, PluginConfigVersion> = {
  "0.1.9": "2023-08-20",
  "0.2.0": "2023-08-20",
  "0.2.1": "2023-08-20",
  "0.2.2": "2023-08-20",
  "0.2.3": "2023-08-20",
  "0.2.4": "2023-08-20",
  "0.2.5": "2023-08-20",
  "0.2.6": "2023-08-20",
  "0.2.7": "2023-08-20",
  "0.2.8": "2023-08-20",
  "0.2.9": "2023-08-20",
  "0.3.0": "2023-08-20",
  "0.3.1": "2023-08-20",
  "0.3.2": "2023-08-20",
  "0.3.3": "2023-08-20",
  "0.4.0": "2023-08-20",
  "0.4.1": "2023-08-20",
  "0.4.2": "2023-08-20",
  "0.4.3": "2023-08-20",
  "0.4.4": "2023-08-20",
  "0.4.5": "2023-08-20",
  "1.0.0": "2024-05-05",
};
